import { Button } from "@mui/material";
import { ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { trackEvent } from "src/hooks/useInstrumentation";
import { isV2Application } from "src/pages/BackgroundSurveyScreen/types/ApplicationWizardTypes";
import resolveRejectedApplicationDetails from "src/pages/Common/resolveRejectedApplicationDetails";
import { useCurrentUser } from "src/SessionBoundary";
import ConfirmDialog from "src/shared/components/ConfirmDialog";
import { programAreas, rejectionCopy } from "../copy";
import useApplicationResultReferralVariant from "../hooks/useApplicationResultReferralVariant";
import {
  hasSeenApplicationResultAsk,
  markApplicationResultAskSeen,
} from "../utils/askSeenMark";
import ReferralAskScreen from "./ReferralAskScreen";

type Props = {
  surface: "rejected" | "waitlist";
  /**
   * The caller's own condition for this surface (e.g. "really waitlisted").
   * When false the gate renders `children` and never reads the flag.
   */
  active: boolean;
  /** Today's screen, shown after the ask (and always in control). */
  children: ReactElement;
};

/**
 * Wraps the rejected and waitlist screens for `application-result-referral-
 * ask`. Test-arm students see the referral ask once, then today's screen.
 * Everyone else sees today's screen untouched.
 */
export default function ApplicationResultReferralGate({
  surface,
  active,
  children,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useCurrentUser();
  const [askPending, setAskPending] = useState(
    () => !hasSeenApplicationResultAsk(user.uid)
  );
  const [confirmUpdateOpen, setConfirmUpdateOpen] = useState(false);
  const { variant, resolved } = useApplicationResultReferralVariant(
    active && askPending
  );
  const showAsk = active && askPending && resolved && variant === "test";

  useEffect(() => {
    if (showAsk) markApplicationResultAskSeen(user.uid);
  }, [showAsk, user.uid]);

  // Brief hold (at most ~3s) while an eligible student's arm resolves.
  if (!resolved) return null;
  if (!showAsk) return children;

  const finish = () => setAskPending(false);

  if (surface === "waitlist") {
    return (
      <ReferralAskScreen
        surface="waitlist"
        reason={programAreas(t)}
        followUp={t("We'll let you know if we open near you.")}
        onDone={finish}
      />
    );
  }

  const disqualifiers =
    user.application && isV2Application(user.application)
      ? user.application.publicEligibilityDecision?.disqualifiers ?? []
      : [];
  const copy = rejectionCopy(
    t,
    disqualifiers,
    resolveRejectedApplicationDetails(user)
  );

  // Same confirm step and event as the disabled-account screen's "Update
  // Application" button, so update funnels compare across arms.
  const handleConfirmUpdate = () => {
    setConfirmUpdateOpen(false);
    trackEvent("Rejected Application Screen - CLicked on Edit Application", {
      userId: user.uid,
      source: "referral_ask",
    });
    navigate("/onboarding");
  };

  return (
    <>
      <ReferralAskScreen
        surface="rejected"
        reason={copy.reason}
        followUp={
          <>
            {copy.fixLead}{" "}
            <Button
              variant="text"
              onClick={() => setConfirmUpdateOpen(true)}
              sx={{
                p: 0,
                minWidth: 0,
                verticalAlign: "baseline",
                font: "inherit",
                fontWeight: 500,
                textDecoration: "underline",
              }}
            >
              {copy.linkLabel}
            </Button>
          </>
        }
        onDone={finish}
      />
      <ConfirmDialog
        open={confirmUpdateOpen}
        title={t("Update Application")}
        description={t(
          "We trust you! Your application information will be reviewed and verified when we reach the enrollment stage."
        )}
        onConfirm={handleConfirmUpdate}
        onCancel={() => setConfirmUpdateOpen(false)}
        buttonColor="primary"
      />
    </>
  );
}
